import React, { FC, useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SafeECharts from './SafeECharts';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { formatNumber } from '../utils/formatters';
import { RangePreset } from '../types';

type RoomNightAnalysisProps = {
  data: any;
  loading: boolean;
  selectedManager: string | number;
  managers: any[];
  rangePreset: RangePreset;
  rangeOptions: Array<{ id: string; name: string }>;
  onManagerChange: (value: string | number) => void;
  onRangeChange: (value: RangePreset) => void;
  onCustomRangeClick: (value: string | number) => void;
};

const tableCellSx = { py: 1.1, px: 1.25 };
const headerCellSx = {
  ...tableCellSx,
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  backgroundColor: '#F8FAFC',
};

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const getValue = (row: any, keys: string[], fallback: any = '-') => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '') {
      return row[key];
    }
  }
  return fallback;
};

const getNumber = (row: any, keys: string[]) => Number(getValue(row, keys, 0)) || 0;

const getSummaryNumber = (summary: any, keys: string[]) => getNumber(summary, keys);

const formatCurrency = (value?: number) => formatCurrencyWithSymbol(Number(value || 0));

const chartTooltip = {
  trigger: 'axis',
  backgroundColor: '#FFFFFF',
  borderColor: '#E2E8F0',
  borderWidth: 1,
  textStyle: { color: '#0F172A', fontSize: 12 },
  extraCssText: 'box-shadow: 0 10px 24px rgba(15,23,42,0.12); border-radius: 8px;',
};

const buildRoomMealPlanMix = (bookingDetails: any[], roomTypeMix: any[], mealPlanMix: any[]) => {
  const sourceRows = bookingDetails.length > 0 ? bookingDetails : [...roomTypeMix, ...mealPlanMix];
  const grouped = new Map<string, any>();

  sourceRows.forEach(row => {
    const roomTypeName = getValue(row, ['roomTypeName', 'roomType', 'type', 'name'], 'Unspecified');
    const mealPlanName = getValue(row, ['mealPlanName', 'mealPlan', 'packageName'], 'Unspecified');
    const key = `${roomTypeName}__${mealPlanName}`;
    const current = grouped.get(key) || {
      roomTypeName,
      mealPlanName,
      roomNights: 0,
      roomRevenue: 0,
      bookingIds: new Set<string | number>(),
    };

    current.roomNights += getNumber(row, ['roomNights', 'roomNight', 'nights']);
    current.roomRevenue += getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue']);
    current.bookingIds.add(getValue(row, ['bookingId', 'bookingNo', 'bookingNumber'], key));
    grouped.set(key, current);
  });

  return Array.from(grouped.values())
    .map(row => ({
      ...row,
      label: `${row.roomTypeName} with ${row.mealPlanName}`,
      bookingCount: row.bookingIds.size,
    }))
    .sort((a, b) => b.roomNights - a.roomNights);
};

const KpiCard = ({
  label,
  value,
  helper,
  icon,
}: {
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
}) => (
  <Card
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
      height: '100%',
    }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '8px',
          bgcolor: '#ECFDF5',
          color: '#047857',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Stack>
  </Card>
);

const MixList = ({
  title,
  icon,
  rows,
  labelKeys,
}: {
  title: string;
  icon: React.ReactNode;
  rows: any[];
  labelKeys: string[];
}) => {
  const topRows = rows.slice(0, 6);
  const maxNights = Math.max(...topRows.map(row => getNumber(row, ['roomNights', 'roomNight', 'nights'])), 1);

  return (
    <Card sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>

      {topRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No data available for this period.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {topRows.map((row, index) => {
            const label = getValue(row, labelKeys, 'Unspecified');
            const nights = getNumber(row, ['roomNights', 'roomNight', 'nights']);
            const revenue = getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue']);
            return (
              <Box key={`${label}-${index}`}>
                <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumber(nights)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(nights / maxNights) * 100}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { bgcolor: index % 2 === 0 ? '#0EA5E9' : '#10B981' },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Revenue {formatCurrency(revenue)}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Card>
  );
};

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(15,23,42,0.06)', height: '100%', width: '100%' }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
      {title}
    </Typography>
    <Box sx={{ height: { xs: 300, md: 340 }, minWidth: 0 }}>{children}</Box>
  </Card>
);

const SimpleTable = ({
  title,
  rows,
  columns,
  emptyMessage,
  minWidth = 720,
}: {
  title: string;
  rows: any[];
  columns: Array<{ label: string; render: (row: any) => React.ReactNode; align?: 'left' | 'right' | 'center' }>;
  emptyMessage: string;
  minWidth?: number;
}) => (
  <Card
    sx={{
      p: 2.5,
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    ) : (
      <Box sx={{ overflowX: 'auto', flex: 1 }}>
        <Table sx={{ minWidth }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.label} align={column.align || 'left'} sx={headerCellSx}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row?.id || row?.bookingId || `${title}-${index}`} hover>
                {columns.map(column => (
                  <TableCell key={column.label} align={column.align || 'left'} sx={tableCellSx}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
  </Card>
);

const RoomNightAnalysis: FC<RoomNightAnalysisProps> = ({
  data,
  loading,
  selectedManager,
  managers,
  rangePreset,
  rangeOptions,
  onManagerChange,
  onRangeChange,
  onCustomRangeClick,
}) => {
  const summary = data?.summary || data || {};
  const roomTypeMix = asArray(data?.roomTypeMix || data?.roomTypes);
  const mealPlanMix = asArray(data?.mealPlanMix || data?.mealPlans);
  const managerWise = asArray(data?.managerWise || data?.managers);
  const companyWise = asArray(data?.companyWise || data?.companies);
  const trend = asArray(data?.trend || data?.dateTrend || data?.periodTrend);
  const allBookingDetails = asArray(data?.bookingDetails || data?.bookings);
  const bookingDetails = allBookingDetails.slice(0, 20);
  const roomMealPlanMix = buildRoomMealPlanMix(allBookingDetails, roomTypeMix, mealPlanMix);

  const totalRoomNights = getSummaryNumber(summary, ['totalRoomNights', 'roomNights']);
  const totalRoomRevenue = getSummaryNumber(summary, ['totalRoomRevenue', 'roomRevenue', 'revenue']);
  const avgRoomRate =
    getSummaryNumber(summary, ['averageRoomRate', 'avgRoomRate', 'arr']) ||
    (totalRoomNights ? totalRoomRevenue / totalRoomNights : 0);
  const totalBookings = getSummaryNumber(summary, ['totalBookings', 'bookingCount', 'bookings']);
  const hasData =
    totalRoomNights > 0 ||
    roomTypeMix.length > 0 ||
    mealPlanMix.length > 0 ||
    managerWise.length > 0 ||
    companyWise.length > 0;

  const trendChartOption = useMemo(
    () => ({
      tooltip: {
        ...chartTooltip,
        formatter: (params: any[]) => {
          const rows = Array.isArray(params) ? params.filter(Boolean) : [];
          if (!rows.length) return '';
          const label = rows[0]?.axisValue || '';
          return [
            `<strong>${label}</strong>`,
            ...rows.map(item =>
              item.seriesName === 'Room Revenue'
                ? `${item.marker || ''} ${item.seriesName}: ${formatCurrency(Number(item.value || 0))}`
                : `${item.marker || ''} ${item.seriesName}: ${formatNumber(Number(item.value || 0))}`,
            ),
          ].join('<br/>');
        },
      },
      legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: '#475569' } },
      grid: { left: 44, right: 60, top: 46, bottom: 42 },
      xAxis: {
        type: 'category',
        data: trend.map(row => getValue(row, ['label', 'period', 'date', 'businessDate'])),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        axisLabel: { color: '#64748B', fontSize: 11 },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Nights',
          nameTextStyle: { color: '#64748B', fontSize: 11 },
          splitLine: { lineStyle: { color: '#E2E8F0', type: 'dashed' } },
          axisLabel: { color: '#64748B', formatter: (value: number) => formatNumber(value) },
        },
        {
          type: 'value',
          name: 'Revenue',
          nameTextStyle: { color: '#64748B', fontSize: 11 },
          splitLine: { show: false },
          axisLabel: { color: '#64748B', formatter: (value: number) => formatCurrency(value) },
        },
      ],
      series: [
        {
          name: 'Room Nights',
          type: 'bar',
          data: trend.map(row => getNumber(row, ['roomNights', 'roomNight', 'nights'])),
          barWidth: 22,
          itemStyle: { color: '#0EA5E9', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Room Revenue',
          type: 'line',
          yAxisIndex: 1,
          data: trend.map(row => getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue'])),
          smooth: true,
          symbolSize: 7,
          lineStyle: { color: '#10B981', width: 3 },
          itemStyle: { color: '#10B981' },
        },
      ],
    }),
    [trend],
  );

  const managerChartRows = useMemo(
    () =>
      [...managerWise]
        .sort((a, b) => getNumber(b, ['roomNights', 'roomNight', 'nights']) - getNumber(a, ['roomNights', 'roomNight', 'nights']))
        .slice(0, 8)
        .reverse(),
    [managerWise],
  );

  const managerChartOption = useMemo(
    () => ({
      tooltip: {
        ...chartTooltip,
        trigger: 'item',
        formatter: (item: any) => {
          if (!item || item.dataIndex == null) return '';
          const row = managerChartRows[item.dataIndex] || {};
          return [
            `<strong>${getValue(row, ['managerName', 'userName', 'name'])}</strong>`,
            `Room Nights: ${formatNumber(getNumber(row, ['roomNights', 'roomNight', 'nights']))}`,
            `Revenue: ${formatCurrency(getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue']))}`,
            `Top Room: ${getValue(row, ['topRoomType', 'preferredRoomType', 'roomTypeName'], '-')}`,
          ].join('<br/>');
        },
      },
      grid: { left: 120, right: 28, top: 18, bottom: 24 },
      xAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#E2E8F0', type: 'dashed' } },
        axisLabel: { color: '#64748B', formatter: (value: number) => formatNumber(value) },
      },
      yAxis: {
        type: 'category',
        data: managerChartRows.map(row => getValue(row, ['managerName', 'userName', 'name'])),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: '#475569', fontSize: 11, width: 106, overflow: 'truncate' },
      },
      series: [
        {
          name: 'Room Nights',
          type: 'bar',
          data: managerChartRows.map(row => getNumber(row, ['roomNights', 'roomNight', 'nights'])),
          barWidth: 18,
          itemStyle: { color: '#2563EB', borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right',
            color: '#334155',
            fontSize: 11,
            formatter: (item: any) => formatNumber(Number(item.value || 0)),
          },
        },
      ],
    }),
    [managerChartRows],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
      <Card sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Room Night Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Room demand, revenue, meal plans and account contribution for the selected stay period.
          </Typography>
        </Box>
      </Card>

      {loading && (
        <Card sx={{ p: 3, borderRadius: 2 }}>
          <LinearProgress sx={{ mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Loading room night analysis...
          </Typography>
        </Card>
      )}

      {!loading && !hasData && (
        <Card sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            No room night data found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try another executive or date range once room bookings are available.
          </Typography>
        </Card>
      )}

      {!loading && hasData && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard label="Total Room Nights" value={formatNumber(totalRoomNights)} icon={<HotelOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                label="Total Room Revenue"
                value={formatCurrency(totalRoomRevenue)}
                helper="Base, meals and configured room components"
                icon={<TrendingUpOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard label="Average Daily Rate" value={formatCurrency(avgRoomRate)} icon={<BedOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard label="Room Bookings" value={formatNumber(totalBookings)} icon={<CalendarMonthOutlinedIcon />} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ChartCard title="Daily Room Night Trend">
                {trend.length > 0 ? (
                  <SafeECharts option={trendChartOption} style={{ width: '100%', height: '100%' }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No trend data available.
                  </Typography>
                )}
              </ChartCard>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MixList
                title="Room Type & Meal Plan Mix"
                icon={<HotelOutlinedIcon />}
                rows={roomMealPlanMix}
                labelKeys={['label']}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
              <ChartCard title="Sales Executive Room Performance">
                {managerChartRows.length > 0 ? (
                  <SafeECharts option={managerChartOption} style={{ width: '100%', height: '100%' }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No manager-wise room data available.
                  </Typography>
                )}
              </ChartCard>
            </Grid>
            <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
              <SimpleTable
                title="Sales Executive Room Details"
                rows={managerWise}
                emptyMessage="No manager-wise room data available."
                minWidth={520}
                columns={[
                  {
                    label: 'Manager',
                    render: row => {
                      const managerName = getValue(row, ['managerName', 'userName', 'name']);
                      return (
                        <Tooltip title={managerName} arrow placement="top">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: 650,
                              maxWidth: { xs: 120, md: 140 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                            {managerName}
                          </Typography>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    label: 'Room Nights',
                    align: 'right',
                    render: row => formatNumber(getNumber(row, ['roomNights', 'roomNight', 'nights'])),
                  },
                  {
                    label: 'Revenue',
                    align: 'right',
                    render: row => formatCurrency(getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue'])),
                  },
                  {
                    label: 'Top Room',
                    render: row => (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={getValue(row, ['topRoomType', 'preferredRoomType', 'roomTypeName'], '-')}
                      />
                    ),
                  },
                ]}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SimpleTable
                title="Company & Branch Room Performance"
                rows={companyWise}
                emptyMessage="No company-wise room data available."
                columns={[
                  {
                    label: 'Company',
                    render: row => (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 650 }}>
                            {getValue(row, ['companyName', 'accountName', 'name'])}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getValue(row, ['branchName'], '')}
                          </Typography>
                        </Box>
                      </Stack>
                    ),
                  },
                  {
                    label: 'Room Nights',
                    align: 'right',
                    render: row => formatNumber(getNumber(row, ['roomNights', 'roomNight', 'nights'])),
                  },
                  {
                    label: 'Revenue',
                    align: 'right',
                    render: row => formatCurrency(getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue'])),
                  },
                  {
                    label: 'Preferred',
                    render: row => getValue(row, ['preferredRoomType', 'topRoomType', 'roomTypeName'], '-'),
                  },
                ]}
              />
            </Grid>
          </Grid>

          <SimpleTable
            title="Daily Room Night Trend"
            rows={trend}
            emptyMessage="No room-night trend data available."
            columns={[
              { label: 'Period', render: row => getValue(row, ['label', 'period', 'date', 'businessDate']) },
              {
                label: 'Room Nights',
                align: 'right',
                render: row => formatNumber(getNumber(row, ['roomNights', 'roomNight', 'nights'])),
              },
              {
                label: 'Room Revenue',
                align: 'right',
                render: row => formatCurrency(getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue'])),
              },
              {
                label: 'Bookings',
                align: 'right',
                render: row => formatNumber(getNumber(row, ['bookingCount', 'bookings', 'totalBookings'])),
              },
            ]}
          />

          <SimpleTable
            title="Booking Room Details"
            rows={bookingDetails}
            emptyMessage="No booking drilldown data available."
            columns={[
              { label: 'Booking', render: row => getValue(row, ['bookingNo', 'bookingNumber', 'bookingId']) },
              { label: 'Company', render: row => getValue(row, ['companyName', 'accountName']) },
              { label: 'Room Type', render: row => getValue(row, ['roomTypeName', 'roomType', 'type']) },
              { label: 'Meal Plan', render: row => getValue(row, ['mealPlanName', 'mealPlan', 'packageName']) },
              {
                label: 'Room Nights',
                align: 'right',
                render: row => formatNumber(getNumber(row, ['roomNights', 'roomNight', 'nights'])),
              },
              {
                label: 'Revenue',
                align: 'right',
                render: row => formatCurrency(getNumber(row, ['roomRevenue', 'revenue', 'totalRevenue'])),
              },
            ]}
          />
        </>
      )}
    </Box>
  );
};

export default RoomNightAnalysis;
